// @flow
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Switch, Route, useHistory, useParams} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {
  Typography,
  Hidden,
  IconButton,
  Divider,
  Grid,
  useMediaQuery,
  ClickAwayListener,
  Tooltip,
  Button,
  ButtonGroup,
  MenuItem,
  Popper,
  Grow,
  Paper,
  MenuList,
  Snackbar,
  Collapse,
} from '@material-ui/core';
import {
  ArrowBack,
  ArrowBackIosRounded,
  ArrowDropDown,
  ArrowForwardIosRounded,
  ArrowRight,
  AttachFile,
  Close,
  DeleteOutline,
  Edit,
  InfoOutlined,
  ReportProblemOutlined,
  Send,
} from '@material-ui/icons';
import ReceiptContainer from '../containers/ReceiptContainer';
import MapModal from '../components/MapModal/MapModal';
import PendingModal from './modals/PendingModal';
import EmailModal from './modals/EmailModal';
import CancelModal from './modals/CancelModal';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {colors} from '../../../assets/styles/colors';
import CustomAlert from '../../../elements/CustomAlert/CustomAlert';
import {getImageUrl} from '../../../utils/image_provider';
import {getFileDataUrl} from '../../file-storage/utils';
import {getCustomDomain, useIsFeatureInTier} from '../../../utils/component';
import {generateWhatsappLink} from '../../../utils/share';
import receiptIcon from '../../../assets/img/receiptIcon.png';
import whatsAppIcon from '../../../assets/img/WhatsApp.png';
import copyIcon from '../../../assets/img/copyIcon.png';
import preOrderSteps from '../../../assets/img/preOrderSteps.png';
import SendIcon from '@material-ui/icons/Send';
import ShippingLabel from './ShippingLabel/ShippingLabel';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import TierRestrictionModal from '../../../elements/TierRestrictionModal/TierRestrictionModal';
import AlertDueDateTier from '../../tiers/components/AlertDueDateTier/AlertDueDateTier';
import {usePage} from '../../page/utils';
import parsePhoneNumber from 'libphonenumber-js';
import {commaFormat} from '../../../utils/number';
import StockErrorsModal from './modals/StockErrorsModal';
import {
  formatAddressString,
  getAddressFromShippingDetail,
} from '../../map/utils';
import EditCustomerInfoModal from './modals/EditCustomerInfoModal';
import EditDeliveryInfoModal from './modals/EditDeliveryInfoModal';
import EditBillingInfoModal from './modals/EditBillingInfoModal';
import EditPaymentInfoModal from './modals/EditPaymentInfoModal';
import EditAdditionalInfoModal from './modals/EditAdditionalInfoModal';
import EditOrderItemsContainer from '../containers/EditOrderItemsContainer';

import {
  ORDER_STATE_STRINGS,
  ORDER_STATE_COLORS,
  ORDER_STATES,
  INPUT_TYPE,
  MAP_PAYMENT_METHOD,
} from '../../../entities';
import type {
  Order,
  OrderAdditionalInformation,
  User,
  Store,
  OrderState,
  PaymentMethod,
  StoreInformation,
  Coupon,
  OrderStateMessageConfig,
  Currency,
  Collaborator,
  OrderComment,
} from '../../../entities';

import {
  formatDateToCustomFormat,
  useOrderItemsTaxes,
  useCurrentOrder,
  canGoPrevOrderState,
  getAvailableOrderStates,
} from '../utils';
import analytics from '../../../analytics';
import StateChangeMessageModal from '../../orders/components/StateChangeMessageModal';
import CustomSelect from '../../../elements/CustomSelect/CustomSelect';
import CustomTextField from '../../../elements/CustomTextField/CustomTextField';
import CustomModal from '../../../elements/CustomModal/CustomModal';
import ConfirmModal from '../../../elements/Modals/ConfirmModal';
import {AdditionalInformationProvider} from '../../configure/sales-process/context/AdditionalInformationContext';

import type {OrderStockError} from '../duck';
import type {OrderRequest} from '../api';
import {checkInvoiceStatus, getOrderStockErrors} from '../promises/promises';
import EditOrderProductsErrorsModal from './modals/EditOrderProductsErrorsModal';
import PreOrderAdvice from './PreOrderAdvice/PreOrderAdvice';
import OrderTaxInvoiceSection from './OrderTaxInvoiceSection';
import CopyIcon from '../../../elements/icons/CopyIcon';
import TiendadaTooltip from '../../../elements/tiendada-tooltip';
import {copyTextToClipboard} from '../../../utils/dom/clipboard';
import {classNames} from '../../../utils/react/className';
import {useStoreStockSetting} from '../../configure/sales-process/hooks/useStoreStockSetting';

type Props = {
  fetchOrder: ({orderId: string, forVoucher?: boolean}) => void,
  updateOrderState: ({orderRequest: OrderRequest}) => void,
  updateOrderStateSuccess?: Order,
  fetchPreOrder: ({orderId: string, forVoucher?: boolean}) => void,
  updatePreOrderState: ({orderRequest: OrderRequest}) => void,
  order?: Order,
  isFetching: boolean,
  isSaving: boolean,
  store?: Store,
  user?: User,
  fetchOrderStockErrors: ({orderId: string}) => void,
  isFetchingOrderStockErrors?: boolean,
  fetchOrderStockErrorsSuccess: boolean,
  orderStockErrors: OrderStockError[],
  updateOrderStateError: any,
  sendEmail: ({orderId: string}) => void,
  sendEmailSuccess?: boolean,
  paymentMethod?: PaymentMethod,
  paymentMethods?: PaymentMethod[],
  information?: StoreInformation,
  getPaymentMethod: ({id: string}) => void,
  resetOrderState: () => void,
  editOrderSuccess?: boolean,
  editOrderError?: string,
  resetManualOrderState: () => void,
  resetPartialFilters: () => void,
  coupons: Coupon[],
  fetchCoupons: () => void,
  orderStateMessages?: OrderStateMessageConfig[],
  getOrderStateMessages: () => void,
  currency: Currency,
  fetchCollaborators: ({collaboratorsRequest: Object}) => void,
  collaborators: Collaborator[],
  fetchCollaborator: ({collaboratorId: string}) => void,
  collaborator?: Collaborator,
  updateOrderCollaborator: ({orderRequest: OrderRequest}) => void,
  saveOrderComment: ({
    orderCommentRequest: {
      orderId: string,
      comment: string,
    },
  }) => void,
  fetchOrderComments: ({orderId: string}) => void,
  orderComments?: OrderComment[],
  updateOrderComment: ({
    orderCommentRequest: {
      orderId: string,
      commentId: string,
      comment: string,
    },
  }) => void,
  deleteOrderComment: ({
    orderCommentRequest: {
      orderId: string,
      commentId: string,
    },
  }) => void,
  saveOrderCommentSuccess?: boolean,
  deleteOrderCommentSuccess?: boolean,
  updateOrderCommentSuccess?: boolean,
};

const useStyles = accentColor =>
  makeStyles(theme => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      background: '#F8F9FB',
    },
    navBackground: {
      [theme.breakpoints.down('sm')]: {
        background: 'white !important',
      },
    },
    headerWrapper: {
      display: 'flex',
      alignItems: 'center',
      padding: '.5rem .5rem 0 .5rem',
      [theme.breakpoints.up('sm')]: {
        padding: '.75rem .75rem 0 .75rem',
      },
    },
    title: {
      fontSize: '24px',
      fontWeight: 600,
      color: 'black',
    },
    backButton: {
      background: 'white',
      padding: '4px',
      margin: '0 16px 0 0',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    },
    button: {
      textTransform: 'none',
      whiteSpace: 'nowrap',
      minWidth: 'unset',
      color: '#55556D',
    },
    alert: {
      maxWidth: 1300,
      margin: theme.spacing(1, 4, 0, 4),
      [theme.breakpoints.down('xs')]: {
        margin: theme.spacing(1, 2, 0, 2),
      },
    },
    stateChangeAlert: {
      borderRadius: '12px',
      background: 'white',
      boxShadow: '0px 4px 30px rgba(139, 139, 139, 0.25)',
      padding: theme.spacing(2),
      paddingRight: theme.spacing(6),
      display: 'flex',
      alignItems: 'center',
    },
    stateChangeAlertTitle: {
      marginLeft: theme.spacing(1),
      fontWeight: 600,
    },
    alertStatePill: {
      marginLeft: theme.spacing(2),
      // width: 96,
      color: 'white',
      fontSize: '0.875rem',
      backgroundColor: accentColor && accentColor.main,
      borderRadius: '12px',
      padding: '2px 13px',
      textAlign: 'center',
    },
    content: {
      display: 'flex',
      alignItems: 'flex-start',
      maxWidth: 1300,
      padding: '.5rem',
      gap: '.5rem',
      marginBottom: '3rem',
      [theme.breakpoints.up('sm')]: {
        padding: '.75rem',
        gap: '.75rem',
        marginBottom: '0',
      },
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        alignItems: 'center',
      },
    },
    orderLeftContent: {
      flex: '1 2 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: 220,
      maxWidth: 400,
      borderRadius: '12px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      padding: theme.spacing(5, 3, 3, 3),
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(3, 2, 2, 2),
      },
      background: 'white',
      flexShrink: 0,
    },
    maxWithWrapper: {
      maxWidth: '300px',
      [theme.breakpoints.down('xs')]: {
        maxWidth: '400px',
      },
    },
    orderRightContent: {
      flex: '1 2 60%',
      // flexGrow: 3,
      // flexShrink: 3,
      [theme.breakpoints.down('md')]: {
        flex: '1 2 60%',
      },
      [theme.breakpoints.down('xs')]: {
        marginLeft: 0,
      },
      borderRadius: '12px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      padding: theme.spacing(2),
      background: 'white',
    },
    receiptLeftContent: {
      flex: '1 2 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginTop: theme.spacing(2),
      borderRadius: '12px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      padding: theme.spacing(5, 3, 3, 3),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(3, 2, 2, 2),
        width: '100%',
      },
      background: 'white',
    },
    statePill: {
      // width: 96,
      fontWeight: 500,
      color: accentColor && accentColor.main,
      backgroundColor: accentColor && accentColor.light,
      borderRadius: '12px',
      padding: '2px 13px',
      textAlign: 'center',
    },
    orderNumber: {
      fontWeight: 600,
      whiteSpace: 'nowrap',
      fontSize: '1.125rem',
      color: colors.grey900,
      margin: theme.spacing(1, 0),
    },
    date: {color: colors.grey600, marginBottom: theme.spacing(2)},
    manualOrderBadge: {
      width: 'fit-content',
      background: colors.grey300,
      borderRadius: '4px',
      padding: '2px 6px 0px 6px',
      color: colors.grey700,
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    saleChannelDropdown: {
      width: '100%',
      marginTop: theme.spacing(2),
    },
    saleChannelTextfield: {
      marginTop: theme.spacing(1),
    },
    saleChannelButton: {
      marginTop: theme.spacing(2),
      borderRadius: 4,
      width: '100%',
    },
    commentLogDate: {
      marginBottom: theme.spacing(1),
      '&:first-child': {
        marginTop: theme.spacing(2),
      },
      color: colors.grey600,
      fontSize: '0.75rem',
      fontWeight: 500,
    },
    orderCommentButton: {
      margin: theme.spacing(1, 0, 0, 1),
      background: colors.grey300,
      width: 24,
      height: 24,
      '&:hover': {
        background: colors.grey400,
      },
    },
    orderCommentButtonIcon: {
      fontSize: 16,
    },
    commentsSeeMoreButton: {
      width: '100%',
      fontWeight: 500,
      margin: theme.spacing(-2, 'auto'),
    },
    seeMoreCommentsModal: {
      padding: theme.spacing(2, 0),
      maxWidth: 300,
      maxHeight: 500,
      overflow: 'auto',
    },
    seeMoreCommentsModalTitle: {
      fontWeight: 600,
    },
    receipt: {
      marginBottom: theme.spacing(2),
      color: '#00B382',
      padding: 2,
      fontSize: '0.875rem',
    },
    stateChangeButtonWrapper: {
      alignSelf: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      [theme.breakpoints.down('xs')]: {
        position: 'fixed',
        bottom: 90,
        left: 0,
        zIndex: 2,
        width: `calc(100% - ${theme.spacing(4)})`,
        margin: theme.spacing(2),
      },
    },
    automaticPaymentStateChangeDisclaimer: {
      width: '80%',
      background: colors.grey200,
      borderRadius: '4px',
      padding: theme.spacing(1),
      color: colors.grey700,
      fontSize: '0.75rem',
      marginBottom: theme.spacing(1),
      alignSelf: 'flex-end',
    },
    stateChangeButton: {
      height: 40,
      textTransform: 'unset',
      width: '100%',
      fontWeight: 600,
      fontSize: '1rem',
      borderRadius: '20px',
      backgroundColor: accentColor && accentColor.main,
      borderColor: 'rgba(255, 255, 255, 0.25) !important',
      '&:disabled': {
        color: 'white',
        backgroundColor: colors.grey500,
      },
      '&:hover': {
        backgroundColor: accentColor && accentColor.main,
        filter: 'brightness(95%)',
      },
    },
    arrowButton: {
      backgroundColor: accentColor && accentColor.main,
      borderColor: 'rgba(255, 255, 255, 0.25) !important',
      borderRadius: '20px',
      // padding: '1px 6px',
      '&:hover': {
        backgroundColor: accentColor && accentColor.main,
        filter: 'brightness(95%)',
      },
    },
    arrowTooltip: {
      borderRadius: '6px',
      background: colors.grey300,
      color: colors.grey800,
    },
    arrowTooltipArrow: {
      color: colors.grey300,
    },
    noClick: {
      cursor: 'default',
      '&:hover': {
        filter: 'unset',
      },
    },
    statesMenuPaper: {
      margin: '16px auto',
      maxWidth: '100%',
      borderRadius: '8px',
      boxShadow: '0px 2px 18px rgba(25, 41, 48, 0.17)',
      [theme.breakpoints.down('xs')]: {
        maxWidth: 'calc(100% - 32px)',
      },
    },
    statesMenu: {
      padding: 0,
    },
    statesMenuItem: {
      width: 312,
      // padding:'',
      maxWidth: '100%',
      [theme.breakpoints.down('xs')]: {
        width: '400px',
      },
    },
    copyLinkWrap: {
      width: 'fit-content',
      marginTop: theme.spacing(1),
    },
    copyLink: {
      textTransform: 'none',
      borderRadius: 32,
      whiteSpace: 'nowrap',
      minWidth: 'unset',
      color: colors.purple3,
    },
    copyLinkButton: {
      width: '100%',
      height: 40,
      textTransform: 'none',
      borderRadius: '8px',
      whiteSpace: 'nowrap',
      minWidth: 'unset',
    },
    stateRevertButton: {
      marginTop: theme.spacing(1),
    },
    divider: {
      background: colors.grey200,
      width: '100%',
      margin: theme.spacing(2, 0),
    },
    dividerVertical: {
      margin: theme.spacing(0, 1),
      background: colors.grey300,
    },
    cancelButton: {color: '#E13065', margin: theme.spacing(1)},
    orderDetailsContentColumn: {
      flex: 1,
      flexDirection: 'column',
      [theme.breakpoints.up('lg')]: {
        '&:first-child': {
          marginRight: theme.spacing(2),
        },
      },
    },
    innerOrderDetailContent: {
      display: 'flex',
      flexDirection: 'column',
      // alignItems: 'center',
      borderRadius: '8px',
      background: colors.grey100,
      border: `1px solid ${colors.grey200}`,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    cardContent: {
      borderRadius: '8px',
      background: colors.grey100,
      border: `1px solid ${colors.grey200}`,
      padding: theme.spacing(2),
    },
    marginBottom: {
      marginBottom: theme.spacing(2),
    },
    buttonBack: {
      color: colors.purple,
    },
    innerContentTitle: {
      width: '100%',
      color: colors.grey900,
      fontSize: '0.875rem',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    innerContentText: {
      color: colors.grey800,
      fontSize: '0.875rem',
      margin: '2px 0px',
    },
    additionalInformationContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '.5rem',
      width: '100%',
    },
    additionalInformationItem: {
      width: '100%',
    },
    additionalInformationItemValue: {
      color: colors.grey800,
      fontSize: '0.875rem',
      overflow: 'hidden',
      wordBreak: 'break-word',
    },
    additionalInformationCopyButton: {
      padding: '.5rem',
      color: colors.primary,
    },
    copyLinkArea: {
      marginBottom: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      // alignItems: 'center',
    },
    row: {
      display: 'flex',
      alignItems: 'center',
    },
    copyLinkText: {
      margin: theme.spacing(1),
      fontWeight: 500,
      color: colors.grey800,
      fontSize: '0.75rem',
    },
    labelDscto: {
      color: colors.success,
      fontSize: '0.875rem',
      margin: '2px 0px',
    },
    chipDscto: {
      background: '#E04763',
      fontSize: '0.875rem',
      borderRadius: 24,
      color: 'white',
      padding: '0 8px',
      marginRight: 8,
    },
    textVariant: {
      color: colors.grey600,
      fontWeight: 500,
      fontSize: '12px',
    },
    innerItem: {
      display: 'flex',
      // margin: theme.spacing(1, 0),
    },
    innerItemImage: {
      height: 32,
      width: 32,
      borderRadius: '.5rem',
      marginRight: theme.spacing(1),
      [theme.breakpoints.up('sm')]: {
        borderRadius: '.75rem',
        height: 48,
        width: 48,
      },
      [theme.breakpoints.up('md')]: {
        height: 64,
        width: 64,
      },
      [theme.breakpoints.up('lg')]: {
        height: 72,
        width: 72,
      },
    },
    itemComment: {
      fontSize: '0.75rem',
      color: colors.grey600,
      marginTop: '8px',
      marginBottom: '-8px',
    },
    errorClass: {
      filter: 'grayscale(1)',
      opacity: 0.6,
    },
    errorBadge: {
      display: 'flex',
      alignItems: 'center',
      marginTop: theme.spacing(1),
      borderRadius: '14px',
      width: 'fit-content',
      padding: '4px 12px',
      color: '#B21A47',
      fontSize: '0.75rem',
      fontWeight: 500,
      backgroundColor: '#F5E6EB',
    },
    errorIcon: {
      fontSize: '1.125rem',
      marginRight: 8,
    },
    totalsRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    totalText: {
      fontWeight: 700,
    },
    flatRateAmount: {
      padding: '2px 10px',
      backgroundColor: colors.grey200,
      borderRadius: '12px',
      color: colors.grey600,
      fontSize: '0.875rem',
    },
    bold: {
      fontWeight: 600,
    },
    clientCategoryPill: {
      marginLeft: theme.spacing(1),
      fontSize: '0.75rem',
      fontWeight: 600,
      color: colors.grey800,
      borderRadius: '14px',
      padding: '2px 12px',
      background:
        'linear-gradient(94.89deg, #2E8EDF -3.37%, #BBAB84 -3.37%, #2E8EDF -3.36%, #8ECBFF 51.25%, #2E8EDF 108.99%);',
    },
    whatsappButton: {
      alignSelf: 'center',
      color: colors.success,
    },
    upsellMessage: {
      marginTop: theme.spacing(1),
      padding: theme.spacing(1),
      color: colors.purple3,
      background: colors.lightPurple,
      borderRadius: '12px',
      fontSize: '0.875rem',
      [theme.breakpoints.down('xs')]: {
        paddingRight: 40,
      },
    },
    closeUpsellMsgButton: {
      color: 'inherit',
      position: 'absolute',
      top: 44,
      right: 0,
    },
    chipPRO: {
      backgroundColor: '#F5F0FF',
      color: colors.purple3,
      borderRadius: '20px',
      marginLeft: '8px',
      padding: '5px 10px',
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '22px',
    },
    messageCopy: {
      display: 'none',
      marginLeft: '40px',
      padding: '5px 12px',
      backgroundColor: '#000000df',
      borderRadius: '4px',
      color: '#fff',
    },
    buttonComprobante: {
      marginTop: '12px',
      marginBottom: '12px',
      background: '#F5F0FF',
      borderRadius: '8px',
      color: '#7B39FF',
    },
    buttonShipping: {
      marginTop: '12px',
      marginBottom: '12px',
      borderRadius: '8px',
      border: '#F5F0FF',
    },
    discountedPriceText: {
      textDecoration: 'line-through',
      color: '#9595AB',
    },
    taxPercentage: {
      color: colors.grey600,
      fontSize: 14,
    },
    discountButton: {
      padding: '.25rem 0',
      color: colors.purple,
    },
    discountText: {
      fontSize: '0.85rem',
      display: 'flex',
      alignItems: 'center',
    },
    discountIcon: {
      display: 'flex',
    },
    orderDetailColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    additionalInformationTitle: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  }));

export default usePage<Props>(OrderDetail, 'Order Detail');

const CONTINUE_TO_CHAT = 'CONTINUE_TO_CHAT';
const PAY_ON_DELIVERY = 'PAY_ON_DELIVERY';
const AVAILABLE_STATES_TO_CHECK_INVOICE_STATUS: OrderState[] = [
  'PENDING',
  'CONFIRMED',
];

function ProductOptionsDisplay({options, currency, classes}) {
  const [expanded, setExpanded] = useState(false);
  const [copiedText, setCopiedText] = useState('');
  const maxOptionsToShow = 2;
  const hasMoreOptions = options.length > maxOptionsToShow;
  const optionsToShow = expanded ? options : options.slice(0, maxOptionsToShow);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleCopyText = async text => {
    try {
      await copyTextToClipboard(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(''), 2000); // Clear after 2 seconds
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  };

  return (
    <div style={{marginTop: '4px'}}>
      {optionsToShow.map((option, i) => (
        <div
          key={`option-${i}`}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '4px',
          }}
        >
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '.5rem',
              flexWrap: 'wrap',
            }}
          >
            <Typography
              className={classNames(
                classes.innerContentText,
                classes.textVariant
              )}
              style={{marginBottom: option.text ? '2px' : '0'}}
            >
              {option.name} × {option.quantity}
            </Typography>
            {option.text && (
              <Typography
                className={classNames(
                  classes.innerContentText,
                  classes.textVariant
                )}
                style={{
                  fontSize: '12px',
                  color: '#666',
                  cursor: 'pointer',
                  padding: '2px 4px',
                  borderRadius: '4px',
                  backgroundColor:
                    copiedText === option.text ? '#e8f5e8' : '#f5f5f5',
                  display: 'inline-block',
                  transition: 'background-color 0.2s',
                  whiteSpace: 'nowrap',
                }}
                onClick={() => handleCopyText(option.text)}
                title="Click para copiar"
              >
                {copiedText === option.text ? '✓ Copiado' : option.text}
              </Typography>
            )}
          </div>
          <Typography
            className={classNames(
              classes.innerContentText,
              classes.textVariant
            )}
            style={{fontWeight: 500, marginLeft: '8px'}}
          >
            {commaFormat(
              parseFloat(option.price) * option.quantity,
              currency?.symbol
            )}
          </Typography>
        </div>
      ))}

      {hasMoreOptions && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            marginTop: '4px',
          }}
          onClick={toggleExpanded}
        >
          <Typography
            className={classNames(
              classes.innerContentText,
              classes.textVariant
            )}
            style={{
              color: colors.primary.main,
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {expanded
              ? 'Ver menos'
              : `Ver ${options.length - maxOptionsToShow} más`}
            {expanded ? (
              <ArrowDropDown
                style={{transform: 'rotate(180deg)', fontSize: '16px'}}
              />
            ) : (
              <ArrowDropDown style={{fontSize: '16px'}} />
            )}
          </Typography>
        </div>
      )}
    </div>
  );
}

function OrderDetail(props: Props) {
  const {
    fetchOrder,
    updateOrderState,
    updateOrderStateSuccess,
    updateOrderStateError,
    fetchPreOrder,
    updatePreOrderState,
    order,
    isFetching,
    isSaving,
    user,
    store,
    // sendEmail,
    sendEmailSuccess,
    paymentMethod,
    paymentMethods,
    getPaymentMethod,
    information,
    fetchOrderStockErrors,
    isFetchingOrderStockErrors,
    fetchOrderStockErrorsSuccess,
    orderStockErrors,
    resetOrderState,
    editOrderSuccess,
    editOrderError,
    resetManualOrderState,
    resetPartialFilters,
    coupons,
    fetchCoupons,
    orderStateMessages,
    getOrderStateMessages,
    currency,
    fetchCollaborators,
    collaborators,
    fetchCollaborator,
    collaborator,
    updateOrderCollaborator,
    saveOrderComment,
    fetchOrderComments,
    orderComments,
    updateOrderComment,
    deleteOrderComment,
    saveOrderCommentSuccess,
    deleteOrderCommentSuccess,
    updateOrderCommentSuccess,
  } = props;

  const history = useHistory();

  const {settings: stockSettings} = useStoreStockSetting();

  const {orderId} = useParams();
  const shouldFetchPreOrder = !!orderId && orderId.startsWith('PO-');

  const [editStockErrors, setEditStockErrors] = useState<
    OrderStockError[] | null
  >(null);
  const [openPaymentTooltip, setOpenPaymentTooltip] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const [copyClipboardSuccess, setCopyClipboardSuccess] = useState('');

  const closeConfirmModal = () => setConfirmModalOpen(false);
  const openConfirmModal = () => setConfirmModalOpen(true);

  const {taxInvoiceData} = useCurrentOrder();
  const canGoToPrevState = order && canGoPrevOrderState(order, taxInvoiceData);
  const availableOrderStates =
    order && getAvailableOrderStates(order, taxInvoiceData);

  const accentColor = useMemo(
    () =>
      !shouldFetchPreOrder
        ? (order && order.orderState && ORDER_STATE_COLORS[order.orderState]) ||
          {}
        : order && order.preOrderState && order.preOrderState === 'CANCELLED'
        ? ORDER_STATE_COLORS[order.preOrderState] || {}
        : {main: 'white', light: colors.purple3},
    [shouldFetchPreOrder, order]
  );

  const stateString = useMemo(
    () =>
      !shouldFetchPreOrder
        ? (order &&
            order.orderState &&
            ORDER_STATE_STRINGS[order.orderState]) ||
          ''
        : order && order.preOrderState && order.preOrderState === 'CANCELLED'
        ? 'Pre Pedido Cancelado' || ''
        : 'Pre Pedido',
    [order, shouldFetchPreOrder]
  );

  const dateString = useMemo(
    () =>
      !!order
        ? new Intl.DateTimeFormat('es-PE', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
          }).format(order.createdAt)
        : '',
    [order]
  );

  const sortedAdditionalInformation = useMemo(() => {
    if (!order || !order.additionalInformation) return [];

    const copy = [...order.additionalInformation];
    return copy.sort((a, b) => {
      const aIndex =
        a.additionalInformation.number ??
        a.storeOrderAdditionalInformation.number;

      const bIndex =
        b.additionalInformation.number ??
        b.storeOrderAdditionalInformation.number;

      return aIndex - bIndex;
    });
  }, [order]);

  const classes = useStyles(accentColor)();

  const handlePaymentTooltipClose = () => {
    setOpenPaymentTooltip(false);
  };

  useEffect(() => {
    return () => {
      resetOrderState();
      resetManualOrderState();
      resetPartialFilters();
    };
  }, [resetManualOrderState, resetOrderState, resetPartialFilters]);

  useEffect(() => {
    if (orderId) {
      if (shouldFetchPreOrder) {
        fetchPreOrder({orderId});
      } else fetchOrder({orderId});
    }
  }, [fetchOrder, fetchPreOrder, shouldFetchPreOrder, orderId]);

  const isPreOrder = order && order.type === 'pre-order';

  const [discountDetailOpen, setDiscountDetailOpen] = useState(false);
  const toggleDiscountDetail = () => setDiscountDetailOpen(!discountDetailOpen);

  const orderItemTaxesMap = useOrderItemsTaxes(
    order,
    order && order.type === 'order'
  );

  const getOrderItemPrice = (orderItemId: string, orderItemTaxesMap = {}) => {
    return orderItemTaxesMap[orderItemId] ?? 1;
  };

  useEffect(() => {
    if (
      order &&
      order.type === 'pre-order' &&
      order.preOrderState === 'ORDERED' &&
      order.order
    ) {
      history.push(`/orders/${order.order.id}`);
    }
  }, [history, order]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  useEffect(() => {
    if (!order) return;
    if (!AVAILABLE_STATES_TO_CHECK_INVOICE_STATUS.includes(order.orderState)) {
      return;
    }
    checkInvoiceStatus({orderInvoiceId: order.invoice.invoiceCoId}).then(
      status => {
        if (status === 'PAID') {
          fetchOrder({orderId: order.id});
        }
      }
    );
  }, [fetchOrder, order]);

  useEffect(() => {
    if (order && order.invoice && order.invoice.storePaymentMethodProcessorId) {
      getPaymentMethod({id: order.invoice.storePaymentMethodProcessorId});
    }
  }, [getPaymentMethod, order]);

  const orderStatePill = useMemo(
    () => <div className={classes.statePill}>{stateString}</div>,
    [classes.statePill, stateString]
  );

  const [openToolTip, setOpenToolTip] = useState(false);
  const handleTooltipClose = () => {
    setOpenToolTip(false);
  };

  const [pendingModalOpen, setPendingModalOpen] = useState(false);
  const handlePendingModalClose = useCallback(
    () => setPendingModalOpen(false),
    []
  );
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const handleEmailModalClose = () => setEmailModalOpen(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const handleCancelModalClose = () => setCancelModalOpen(false);

  const [tierRestrictionModal, setTierRestrictionModal] = useState(false);

  const [openMapModal, setOpenMapModal] = useState(false);

  const handleOpenMapModal = () => {
    setOpenMapModal(true);
  };

  const storeHasOrderStatusChangeFeature = useIsFeatureInTier(
    'ORDER_STATUS_CHANGE'
  );

  const hasOrderStatusChangeFeature = storeHasOrderStatusChangeFeature;

  const [messageError, setMessageError] = useState('');

  const getOrderStateIndex = (state: OrderState) => {
    return ORDER_STATES.findIndex(s => s === state);
  };
  const orderStateIndex = useMemo(() => {
    return order && order.orderState && getOrderStateIndex(order.orderState);
  }, [order]);

  const updateOrderStateWithErrorCheck = useCallback(
    (orderRequest: OrderRequest) => {
      if (!orderRequest) return;

      const shouldCheckStock =
        stockSettings?.setting?.orderState === orderRequest.state;

      if (!shouldCheckStock) {
        updateOrderState({orderRequest});
        return;
      }

      const isPreOrder = orderRequest.orderCreationType === 'PRE_ORDER';

      fetchOrderStockErrors({orderId: orderRequest.id, isPreOrder});
      setUpdateOrderStateRequest({orderRequest});
    },
    [fetchOrderStockErrors, stockSettings, updateOrderState]
  );

  const handleUpdateOrderState = useCallback(
    (state: OrderState) => {
      if (isPreOrder) {
        updatePreOrderState({
          orderRequest: {
            id: order.id,
            state: state,
          },
        });
      } else {
        const orderRequest = {
          id: order.id,
          state: state,
          previousState: order.orderState,
          orderCreationType: order.orderCreationType,
        };

        const shouldCheckStock = stockSettings?.setting?.orderState === state;

        if (shouldCheckStock) {
          updateOrderStateWithErrorCheck(orderRequest);
        } else {
          updateOrderState({orderRequest});
        }
      }
    },
    [
      isPreOrder,
      order,
      stockSettings,
      updateOrderState,
      updateOrderStateWithErrorCheck,
      updatePreOrderState,
    ]
  );

  const paymentMethodCard = useMemo(() => {
    return (
      order &&
      order.invoice &&
      paymentMethod &&
      paymentMethod.paymentMethod === 'CARD'
    );
  }, [order, paymentMethod]);

  const handleChangeState = useCallback(
    (state: OrderState) => {
      if (state) {
        if (!storeHasOrderStatusChangeFeature) {
          setTierRestrictionModal(true);
        } else {
          if (
            storeHasOrderStatusChangeFeature &&
            paymentMethod &&
            paymentMethod.enabled
          ) {
            if (order && order.orderState)
              switch (order.orderState) {
                case 'PENDING':
                case 'CONFIRMED':
                  switch (state) {
                    case 'PAID':
                      if (!paymentMethodCard) setPendingModalOpen(true);
                      break;
                    default:
                      handleUpdateOrderState(state);
                      break;
                  }
                  break;
                default:
                  handleUpdateOrderState(state);
                  return;
              }
          } else {
            setMessageError(
              ' Método de pago no está habilitado por favor actualizarlo para continuar.'
            );
          }
        }
      }
    },
    [
      handleUpdateOrderState,
      order,
      paymentMethod,
      paymentMethodCard,
      storeHasOrderStatusChangeFeature,
    ]
  );

  const [urlLogo, setUrlLogo] = useState(undefined);
  useEffect(() => {
    if (!information) return;
    let cancelled = false;
    getFileDataUrl(information.logoId).then(url => {
      if (!cancelled) setUrlLogo(url);
    });
    return () => {
      cancelled = true;
    };
  }, [information]);

  const [
    updateOrderStateRequest,
    setUpdateOrderStateRequest,
  ] = useState<Object>();

  const [openStockErrorsModal, setOpenStockErrorsModal] = useState(false);
  useEffect(() => {
    if (fetchOrderStockErrorsSuccess && updateOrderStateRequest) {
      if (orderStockErrors && orderStockErrors.length)
        setOpenStockErrorsModal(true);
      else {
        updateOrderState(updateOrderStateRequest);
        setUpdateOrderStateRequest();
      }
    }
  }, [
    fetchOrderStockErrorsSuccess,
    handleUpdateOrderState,
    orderStockErrors,
    updateOrderState,
    updateOrderStateRequest,
  ]);
  const handleStockErrorsConfirm = () => {
    if (updateOrderStateRequest) {
      updateOrderState(updateOrderStateRequest);
      setUpdateOrderStateRequest();
    }
  };

  const shippingAddress = useMemo(() => {
    if (order && order.shippingDetail) {
      const {shippingDetail} = order;
      const {
        address1,
        address2,
        admin1 = '',
        admin2 = '',
        admin3 = '',
      } = getAddressFromShippingDetail(shippingDetail);

      const admins = formatAddressString(admin1, admin2, admin3);
      let fullAddress = `${address1}${
        address2 ? ', ' + address2 : ''
      }, ${admins}`;

      if (admin1) return fullAddress;
      else
        return (
          <>
            <div>{address1}.</div>
            <div style={{marginBottom: '4px'}}>{address2}</div>
          </>
        );
    } else return '';
  }, [order]);

  useEffect(() => {
    if (updateOrderStateSuccess || updateOrderStateError) {
      setOpenStockErrorsModal(false);
      setPendingModalOpen(false);
    }
    if (updateOrderStateSuccess) {
      analytics.fireCustomEvent('Order State Changed', undefined, [
        'user-guiding',
      ]);
    }
    if (updateOrderStateError) {
      setOpenStockErrorsModal(false);
    }
  }, [updateOrderStateError, updateOrderStateSuccess]);

  const handleCancel = useCallback(() => {
    if (hasOrderStatusChangeFeature) setCancelModalOpen(true);
    else setTierRestrictionModal(true);
  }, [hasOrderStatusChangeFeature]);

  const hasOrderEditCustomerDataFeature = useIsFeatureInTier(
    'ORDER_EDIT_CUSTOMER_DATA'
  );

  const hasOrderEditItemsFeature = useIsFeatureInTier('ORDER_EDIT_ITEMS');

  // const hasOrderEditCouponFeature = useIsFeatureInTier('ORDER_EDIT_COUPON');
  const hasOrderEditPaymentDataFeature = useIsFeatureInTier(
    'ORDER_EDIT_PAYMENT_DATA'
  );

  const hasOrderEditDeliveryDataFeature = useIsFeatureInTier(
    'ORDER_EDIT_DELIVERY_DATA'
  );

  const hasOrderEditAdditionalInformationFeature = useIsFeatureInTier(
    'ORDER_EDIT_ADDITIONAL_INFORMATION'
  );

  const userHasOrderEditCollaboratorFeature = useIsFeatureInTier(
    'ORDER_EDIT_COLLABORATOR'
  );

  const isCouponActive = useMemo(() => {
    if (order && order.invoice && order.invoice.coupon && coupons) {
      const coupon = coupons.find(c => c.code === order.invoice.coupon);
      return !!coupon && coupon.enabled;
    }
  }, [coupons, order]);
  const canEditOrder = useMemo(() => {
    if (
      order &&
      order.orderState &&
      order.orderState === 'PENDING' &&
      store &&
      order.invoice &&
      (!order.invoice.coupon || isCouponActive)
    )
      return true;
  }, [isCouponActive, order, store]);

  const [editCustomerInfoModal, setEditCustomerInfoModal] = useState(false);
  const [editDeliveryInfoModal, setEditDeliveryInfoModal] = useState(false);
  const [editBillingInfoModal, setEditBillingInfoModal] = useState(false);
  const [editPaymentInfoModal, setEditPaymentInfoModal] = useState(false);
  const [editAdditionalInfoModal, setEditAdditionalInfoModal] = useState(false);
  useEffect(() => {
    if (editOrderSuccess) {
      setEditCustomerInfoModal(false);
      setEditDeliveryInfoModal(false);
      setEditPaymentInfoModal(false);
      setEditAdditionalInfoModal(false);
      closeConfirmModal();
    }
  }, [editOrderSuccess]);

  const stateChangeDisabled = useMemo(() => {
    if (
      !hasOrderStatusChangeFeature ||
      (paymentMethodCard &&
        order &&
        order.orderState &&
        order.orderState === 'CONFIRMED')
    )
      return true;
  }, [hasOrderStatusChangeFeature, order, paymentMethodCard]);

  const updateOrderStateErrorDetail = useMemo(() => {
    if (
      updateOrderStateError &&
      updateOrderStateError.status === 400 &&
      updateOrderStateError.error &&
      updateOrderStateError.error.name === 'OrderItemsStockError'
    )
      return updateOrderStateError.error.detail;
    return null;
  }, [updateOrderStateError]);

  useEffect(() => {
    if (!updateOrderStateError) return;
    if (
      updateOrderStateError === 'NOT_ENOUGH_PRODUCT_VARIANT_STOCK_ERROR' ||
      updateOrderStateError === 'NOT_ENOUGH_PRODUCT_STOCK_ERROR'
    ) {
      fetchOrderStockErrors({orderId: order.id, isPreOrder});
      setOpenStockErrorsModal(true);
    }
  }, [fetchOrderStockErrors, isPreOrder, order, updateOrderStateError]);

  const manualModification = useMemo(() => {
    if (order && order.invoice && order.invoice.detail.total) {
      const totalDiscount = Number(
        order.invoice.detail.discount
          ? order.invoice.detail.discount.total_discount || 0
          : 0
      );

      const totalOrder = Number(order.invoice.detail.total);
      const productsSummary = Number(
        order.invoice.detail.tax_inclusive_products ||
          order.invoice.detail.products
      );
      const shippingPrice = Number(order.invoice.detail.last_mile_shipping);
      // const shippingPromotionPrice = Math.abs(
      //   Number(order.invoice.detail.discount?.shipping_promotion_discount ?? 0)
      // );
      const taxesDiscount = Number(order.invoice.detail.taxes || 0);

      // multiply by 10 and divide by 10 to avoid floating point errors
      const manualSubTotal =
        (productsSummary * 10 +
          totalDiscount * 10 +
          shippingPrice * 10 +
          // shippingPromotionPrice * 10 +
          taxesDiscount * 10) /
        10;

      const value = totalOrder - manualSubTotal;
      const sign = value < 0 ? '-' : '+';

      const absValue = Math.abs(parseFloat(value)).toFixed(0);
      if (Number(absValue) === 0) return undefined;
      else return `${sign}(${commaFormat(Math.abs(value), currency?.symbol)})`;
    } else return undefined;
  }, [currency, order]);

  const [upsellMsg, setUpsellMsg] = useState(true);
  const mobile = useMediaQuery('(max-width:600px)');
  const tablet = useMediaQuery('(max-width:960px)');

  const anchorRef = useRef(null);
  const [open, setOpen] = React.useState(false);
  const handleClickChangeStateButton = event => {
    setOpen(prevOpen => !prevOpen);
  };
  const handleCloseStatesMenu = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const reviewMessage = useMemo(() => {
    if (!store || !order || !order.customer) return '';

    return `Hola ${
      order.customer.firstName
    }, muchas gracias por tu compra. Te agradecería si me puedes dejar una reseña de mi tienda . Lo puedes hacer en este link: https://${getCustomDomain(
      store
    )}/order/${order.id}/review?token=${order.token}`;
  }, [order, store]);

  const [stateChangeAlertOpen, setStateChangeAlertOpen] = useState(false);
  const [
    stateChangeMessageModalOpen,
    setStateChangeMessageModalOpen,
  ] = useState(false);
  const handleChangeMessageModalClose = useCallback(() => {
    setStateChangeMessageModalOpen(false);
  }, []);

  const hasOrderStateChangeMessageFeature = useIsFeatureInTier(
    'ORDER_STATE_CHANGE_MESSAGES'
  );

  useEffect(() => {
    if (hasOrderStateChangeMessageFeature && !orderStateMessages) {
      getOrderStateMessages();
    }
  }, [
    getOrderStateMessages,
    hasOrderStateChangeMessageFeature,
    orderStateMessages,
  ]);

  useEffect(() => {
    if (updateOrderStateSuccess) {
      setStateChangeAlertOpen(true);
      if (hasOrderStateChangeMessageFeature && orderStateMessages) {
        if (
          updateOrderStateSuccess.previousState &&
          getOrderStateIndex(updateOrderStateSuccess.previousState) <
            getOrderStateIndex(updateOrderStateSuccess.orderState)
        ) {
          const stateMessageConfig = orderStateMessages.find(
            config => config.orderState === updateOrderStateSuccess.orderState
          );
          if (stateMessageConfig && stateMessageConfig.enabled)
            setStateChangeMessageModalOpen(true);
        }
      }
    } else {
      setStateChangeAlertOpen(false);
      setStateChangeMessageModalOpen(false);
    }
  }, [
    hasOrderStateChangeMessageFeature,
    orderStateMessages,
    updateOrderStateSuccess,
  ]);

  const [alertOpen, setAlertOpen] = useState<string>('');
  const [alertPayment, setAlertPayment] = useState<string>('');
  useEffect(() => {
    if (editOrderSuccess) setAlertOpen('Orden modificada con éxito');
    else setAlertOpen('');
  }, [editOrderSuccess]);

  const updateOrderStateErrorMessage = useMemo(() => {
    if (
      updateOrderStateError &&
      updateOrderStateError.error &&
      updateOrderStateError.error.name
    ) {
      switch (updateOrderStateError.error.name) {
        case 'OrderItemsStockError':
          return 'Producto no cuenta con stock, por favor añadir unidades';
        case 'UpdateOrderStateError':
          return 'Cambio de estado inválido';
        case 'NullOrderInvoiceCOError':
          return 'NullOrderInvoiceCOError, contáctese con TiendaDa';
        default:
          return 'Error al actualizar el estado';
      }
    }
    return '';
  }, [updateOrderStateError]);

  const canGeneratePaymentLink = useMemo(() => {
    if (!paymentMethods) return;
    return (
      paymentMethods &&
      !!paymentMethods.filter(
        pm => ['CARD', 'OTHERS'].includes(pm.paymentMethod) && pm.enabled
      ).length
    );
  }, [paymentMethods]);

  const sendPaymentLink = paymentLink => {
    if (canGeneratePaymentLink) {
      setAlertPayment('');
      const wsspLink = generateWhatsappLink(order.customer.phone, paymentLink);
      if (window) window.open(wsspLink, '_blank', 'noopener,noreferrer');
    } else {
      setAlertPayment(
        'Por favor configura los métodos de pago que desees aceptar.'
      );
    }
  };

  // const storeHasCollaboratorsFeature = useIsFeatureInTier('STORE_COLLABORATOR');
  // const userHasCollaboratorsFeatureDisabled = useIsFeatureInTier(
  //   'STORE_COLLABORATOR'
  // );
  // const hasCollaboratorsFeature =
  //   storeHasCollaboratorsFeature && !userHasCollaboratorsFeatureDisabled;

  useEffect(() => {
    if (userHasOrderEditCollaboratorFeature && !collaborators)
      fetchCollaborators({collaboratorsRequest: {archived: false}});
  }, [collaborators, fetchCollaborators, userHasOrderEditCollaboratorFeature]);
  const handleChangeOrderCollaborator = (orderId, collaboratorId) => {
    updateOrderCollaborator({
      orderRequest: {
        id: orderId,
        collaboratorId: collaboratorId,
      },
    });
  };

  useEffect(() => {
    if (userHasOrderEditCollaboratorFeature && order && order.collaboratorId)
      fetchCollaborator({collaboratorId: order.collaboratorId});
  }, [fetchCollaborator, order, userHasOrderEditCollaboratorFeature]);

  const [seeMoreCommentsModalOpen, setSeeMoreCommentsModalOpen] = useState(
    false
  );
  const userHasOrderCommentsFeature = useIsFeatureInTier('ORDER_COMMENTS');
  useEffect(() => {
    if (order) {
      fetchOrderComments({orderId: order.id});
    }
  }, [fetchOrderComments, order]);
  const [commentValue, setCommentValue] = useState('');
  const handleSaveOrderComment = () => {
    if (order && commentValue) {
      saveOrderComment({
        orderCommentRequest: {
          orderId: order.id,
          comment: commentValue,
        },
      });
    }
  };
  useEffect(() => {
    if (saveOrderCommentSuccess) setCommentValue('');
  }, [saveOrderCommentSuccess]);
  const handleDeleteOrderComment = comment => {
    if (order)
      deleteOrderComment({
        orderCommentRequest: {
          orderId: order.id,
          commentId: comment.id,
        },
      });
  };

  const [editingCommentIndex, setEditingCommentIndex] = useState(-1);
  const [editingCommentValue, setEditingCommentValue] = useState('');
  const handleClickEditOrderComment = index => {
    if (orderComments) {
      if (editingCommentIndex === index) {
        setEditingCommentIndex(-1);
        setEditingCommentValue('');
      } else {
        setEditingCommentIndex(index);
        setEditingCommentValue(orderComments[index].comment);
      }
    }
  };
  const handleSaveOrderEditingComment = () => {
    if (
      order &&
      editingCommentValue &&
      orderComments &&
      editingCommentIndex >= 0
    ) {
      updateOrderComment({
        orderCommentRequest: {
          orderId: order.id,
          commentId: orderComments[editingCommentIndex].id,
          comment: editingCommentValue,
        },
      });
    }
  };
  useEffect(() => {
    if (updateOrderCommentSuccess) {
      setEditingCommentIndex(-1);
      setEditingCommentValue('');
    }
  }, [updateOrderCommentSuccess]);

  const orderShippingAmount = useMemo(() => {
    if (
      order &&
      order.invoice &&
      order.invoice.detail &&
      order.invoice.detail.last_mile_shipping
    ) {
      // if (order.invoice.detail.discount?.shipping_promotion_discount) {
      //   return (
      //     parseFloat(order.invoice.detail.last_mile_shipping) -
      //     Math.abs(
      //       parseFloat(
      //         order.invoice?.detail?.discount?.shipping_promotion_discount
      //       )
      //     )
      //   );
      // } else {
      return parseFloat(order.invoice.detail.last_mile_shipping);
      // }
    } else return -1;
  }, [order]);

  const stateChangeButton = order &&
    order.orderState &&
    ['PENDING', 'CONFIRMED', 'PAID', 'ON_THE_WAY', 'DELIVERED'].includes(
      order.orderState
    ) && (
      <div className={classes.stateChangeButtonWrapper}>
        {paymentMethodCard && stateChangeDisabled && (
          <div className={classes.automaticPaymentStateChangeDisclaimer}>
            Cambio de estado automático con el pago del cliente. Para cambiarlo
            manual, actualizar método de pago
          </div>
        )}
        <ButtonGroup variant="contained" color="primary" disableElevation>
          <Tooltip
            open={!ORDER_STATES[orderStateIndex - 1] ? false : undefined}
            classes={{
              arrow: classes.arrowTooltipArrow,
              tooltip: classes.arrowTooltip,
            }}
            title={
              ORDER_STATE_STRINGS[ORDER_STATES[orderStateIndex - 1]]
                ? `Anterior estado - ${
                    ORDER_STATE_STRINGS[ORDER_STATES[orderStateIndex - 1]]
                  }`
                : ''
            }
            arrow
          >
            <Button
              className={`${classes.arrowButton} ${
                !ORDER_STATES[orderStateIndex - 1] ? classes.noClick : ''
              }`}
              onClick={() =>
                handleChangeState(ORDER_STATES[orderStateIndex - 1])
              }
              disabled={!canGoToPrevState || !storeHasOrderStatusChangeFeature}
            >
              <ArrowBackIosRounded
                fontSize="small"
                style={{
                  color: !ORDER_STATES[orderStateIndex - 1]
                    ? 'transparent'
                    : undefined,
                }}
              />
            </Button>
          </Tooltip>
          <Button
            ref={anchorRef}
            className={classes.stateChangeButton}
            onClick={handleClickChangeStateButton}
            disabled={!storeHasOrderStatusChangeFeature}
          >
            {stateString}
          </Button>
          <Tooltip
            open={
              !ORDER_STATES[orderStateIndex + 1] ||
              (paymentMethodCard && stateChangeDisabled)
                ? false
                : undefined
            }
            classes={{
              arrow: classes.arrowTooltipArrow,
              tooltip: classes.arrowTooltip,
            }}
            title={
              ORDER_STATE_STRINGS[ORDER_STATES[orderStateIndex + 1]] &&
              ORDER_STATES[orderStateIndex + 1] !== 'CANCELLED'
                ? `Siguiente estado - ${
                    ORDER_STATE_STRINGS[ORDER_STATES[orderStateIndex + 1]]
                  }`
                : ''
            }
            arrow
          >
            <Button
              className={`${classes.arrowButton} ${
                !ORDER_STATES[orderStateIndex + 1] ||
                ORDER_STATES[orderStateIndex + 1] === 'CANCELLED' ||
                (paymentMethodCard && stateChangeDisabled)
                  ? classes.noClick
                  : ''
              }`}
              onClick={() => {
                if (ORDER_STATES[orderStateIndex + 1] !== 'CANCELLED') {
                  handleChangeState(ORDER_STATES[orderStateIndex + 1]);
                }
              }}
              disabled={!storeHasOrderStatusChangeFeature}
              disableRipple={ORDER_STATES[orderStateIndex + 1] === 'CANCELLED'}
            >
              <ArrowForwardIosRounded
                fontSize="small"
                style={{
                  color:
                    !ORDER_STATES[orderStateIndex + 1] ||
                    ORDER_STATES[orderStateIndex + 1] === 'CANCELLED' ||
                    (paymentMethodCard && stateChangeDisabled)
                      ? 'transparent'
                      : undefined,
                }}
              />
            </Button>
          </Tooltip>
        </ButtonGroup>
        {store && upsellMsg && !storeHasOrderStatusChangeFeature && (
          <div className={classes.upsellMessage}>
            Mejora tu plan para guardar el seguimiento de tus pedidos.
            {mobile && (
              <IconButton
                className={classes.closeUpsellMsgButton}
                onClick={() => setUpsellMsg(false)}
              >
                <Close fontSize="small" />
              </IconButton>
            )}
          </div>
        )}
      </div>
    );

  const cancelButton = !!order &&
    ((order.orderState && order.orderState !== 'CANCELLED') ||
      (order.preOrderState && order.preOrderState !== 'CANCELLED')) && (
      <>
        <Divider className={classes.divider} />
        <CustomButton
          className={classes.cancelButton}
          onClick={handleCancel}
          disabled={!storeHasOrderStatusChangeFeature}
          startIcon={<DeleteOutline />}
        >
          Cancelar Pedido
        </CustomButton>
      </>
    );

  const buildAdditionalInformationInfo = (oa: OrderAdditionalInformation) => {
    const {
      additionalInformation,
      storeOrderAdditionalInformation: {inputType},
    } = oa;

    if (inputType === INPUT_TYPE.CHECKLIST) {
      if (additionalInformation.value instanceof Array) {
        return additionalInformation.value.join(', ') ?? '--';
      } else return '--';
    } else if (inputType === INPUT_TYPE.DATE_PICKER) {
      return typeof additionalInformation.value === 'string'
        ? formatDateToCustomFormat(additionalInformation.value)
        : '--';
    }

    return oa.additionalInformation.value[0] ?? '';
  };

  const handleConfirmGoToEdit = () => {
    setEditStockErrors(null);
    resetManualOrderState();
    history.push(`/orders/${order.id}/edit`);
  };

  const handleCheckStockErrors = async () => {
    if (!order) return;
    const isPreOrder = order.orderCreationType === 'PRE_ORDER';
    // fetchOrderStockErrors({orderId: order.id, isPreOrder});
    try {
      const response = await getOrderStockErrors({
        orderId: order.id,
        isPreOrder,
      });
      if (response) {
        setEditStockErrors(response);
      } else {
        handleConfirmGoToEdit();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const invoiceReceiptImage =
    order?.receipts && order.receipts.length > 0
      ? getImageUrl(order.receipts[0].id)
      : null;

  const orderDetailsContent = order && store && (
    <Grid container spacing={2}>
      {order.orderState && orderStateIndex >= getOrderStateIndex('PAID') && (
        <Hidden xsDown>
          <Grid item xs={12} lg={order.shippingDetail ? 6 : 12}>
            {order.voucher && (
              <CustomButton
                className={classes.buttonComprobante}
                fullWidth
                variant="outlined"
                to={`/orders/${order.id}/emit-receipt`}
                style={{marginBottom: '-12px'}}
                startIcon={<img src={receiptIcon} alt="Whatsapp" />}
              >
                Emitir Comprobante
              </CustomButton>
            )}
          </Grid>
          {order.shippingDetail && order.customer && (
            <Grid item xs={12} lg={6}>
              <ShippingLabel
                storeLogo={urlLogo}
                storeName={store.name}
                customer={order.customer}
                order={order}
              />
            </Grid>
          )}
        </Hidden>
      )}
      <Grid item xs={12} lg={6}>
        <div className={classes.innerOrderDetailContent}>
          <Typography className={classes.innerContentTitle}>
            Resumen de Pedido{' '}
            {canEditOrder && hasOrderEditItemsFeature && order && (
              <Hidden smDown>
                <CustomButton
                  className={classes.editButton}
                  startIcon={<Edit />}
                  onClick={() => {
                    handleCheckStockErrors();
                  }}
                >
                  Modificar
                </CustomButton>
              </Hidden>
            )}
          </Typography>
          <Divider className={classes.divider} />
          {order &&
            order.items &&
            order.items.map((item, i) => {
              const orderItemTaxFactor = getOrderItemPrice(
                item.id,
                orderItemTaxesMap
              );

              const error =
                updateOrderStateErrorDetail &&
                (item.productVariantId
                  ? updateOrderStateErrorDetail.productVariantIds.includes(
                      item.productVariantId
                    )
                  : updateOrderStateErrorDetail.productIds.includes(
                      item.productId
                    ));

              return (
                <React.Fragment key={`order-${i}`}>
                  <div
                    className={`${classes.innerItem} ${
                      error ? classes.errorClass : ''
                    }`}
                  >
                    <img
                      className={classes.innerItemImage}
                      src={getImageUrl(item.imageId ? item.imageId : null)}
                      alt=""
                    />
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography className={classes.innerContentText}>
                        {item.name}
                      </Typography>
                      {item.productVariant && (
                        <Typography
                          className={`${classes.innerContentText} ${classes.textVariant}`}
                        >
                          {Object.values(item.productVariant).join('/')}
                        </Typography>
                      )}
                      {item.detail?.productOptions &&
                        item.detail.productOptions.length > 0 && (
                          <ProductOptionsDisplay
                            options={item.detail.productOptions}
                            currency={currency}
                            classes={classes}
                          />
                        )}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography className={classes.innerContentText}>
                          {`Cantidad: ${item.quantity}`}
                        </Typography>
                        {!isPreOrder ? (
                          <Typography
                            className={classes.innerContentText}
                            style={{fontWeight: 500}}
                          >
                            {commaFormat(
                              parseFloat(item.price) *
                                item.quantity *
                                orderItemTaxFactor,

                              currency && currency.symbol
                            )}
                          </Typography>
                        ) : (
                          <div>
                            {Number(item.price) < Number(item.originalPrice) ? (
                              <Typography
                                className={`${classes.innerContentText} ${classes.discountedPriceText}`}
                                style={{fontWeight: 500}}
                              >
                                {commaFormat(
                                  parseFloat(item.originalPrice) *
                                    item.quantity *
                                    orderItemTaxFactor,
                                  currency ? currency.symbol : ''
                                )}
                              </Typography>
                            ) : null}

                            <Typography
                              className={classes.innerContentText}
                              style={{fontWeight: 500}}
                            >
                              {commaFormat(
                                parseFloat(item.price) *
                                  item.quantity *
                                  orderItemTaxFactor,
                                currency ? currency.symbol : ''
                              )}
                            </Typography>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {item.comment ? (
                    <Typography className={classes.itemComment}>
                      {item.comment}
                    </Typography>
                  ) : null}
                  {error ? (
                    <div className={classes.errorBadge}>
                      <ReportProblemOutlined className={classes.errorIcon} />
                      Producto sin stock
                    </div>
                  ) : null}
                  <Divider className={classes.divider} />
                </React.Fragment>
              );
            })}
          <div className={classes.totalsRow}>
            <Typography className={classes.innerContentText}>
              Subtotal
            </Typography>
            <Typography className={classes.innerContentText}>
              <>
                {isPreOrder
                  ? order && order.subTotal
                    ? commaFormat(
                        order.subTotal,
                        currency ? currency.symbol : ''
                      )
                    : '--'
                  : order.invoice &&
                    commaFormat(
                      order.invoice?.detail?.products,
                      currency?.symbol
                    )}
              </>
            </Typography>
          </div>
          {isPreOrder && order && order.promotions && order.promotions?.length
            ? order.promotions.map(pp => (
                <div key={pp.name} className={classes.totalsRow}>
                  <Typography className={classes.labelDscto}>
                    {pp.name || 'Descuento'}
                  </Typography>
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    <Typography className={classes.labelDscto}>
                      {`-${commaFormat(
                        pp.discount ? pp.discount : 0,
                        currency ? currency.symbol : ''
                      )}`}
                    </Typography>
                  </div>
                </div>
              ))
            : null}

          {isPreOrder && order && order.shippingPromotion && (
            <div className={classes.totalsRow}>
              <Typography className={classes.labelDscto}>
                Promoción de Envío
                <br />
                La promoción de envío se aplicará al momento de pagar
              </Typography>
            </div>
          )}
          {isPreOrder && order && order.coupon && (
            <div className={classes.totalsRow}>
              <Typography className={classes.labelDscto}>
                Cupón {`(${order.coupon.code})`}
              </Typography>
              <div style={{display: 'flex', alignItems: 'center'}}>
                <Typography className={classes.labelDscto}>
                  {`-${commaFormat(
                    Number(order.coupon ? order.coupon.totalDiscount : 0),
                    currency.symbol
                  )}`}
                </Typography>
              </div>
            </div>
          )}
          {isPreOrder && order && order.globalDiscount && (
            <div className={classes.totalsRow}>
              <Typography className={classes.labelDscto}>
                Dcto. Manual
              </Typography>
              <div style={{display: 'flex', alignItems: 'center'}}>
                <Typography className={classes.labelDscto}>
                  {`-${commaFormat(
                    Number(
                      order.globalDiscount ? order.globalDiscount.discount : 0
                    ),
                    currency.symbol
                  )}`}
                </Typography>
              </div>
            </div>
          )}
          {/* {isPreOrder &&
            order.taxes?.map((t: InvoiceTax, idx) => (
              <div key={t.tax_id} className={classes.totalsRow}>
                <Typography
                  className={classes.innerContentText}
                  style={{maxWidth: '80%'}}
                >
                  {t.label}{' '}
                  <span className={classes.taxPercentage}>
                    ({parseFloat(Number(t.percentage) * 100).toFixed(0)}%)
                  </span>
                </Typography>
                <Typography className={classes.innerContentText}>
                  {commaFormat(parseFloat(t.amount), currency?.symbol)}
                </Typography>
              </div>
            ))} */}
          {isPreOrder && order ? (
            <>
              <div className={classes.totalsRow}>
                <Typography
                  className={`${classes.innerContentText} ${classes.totalText}`}
                >
                  Total
                </Typography>
                <Typography
                  className={`${classes.innerContentText} ${classes.totalText}`}
                >
                  {commaFormat(
                    order.total ? order.total : '',
                    currency ? currency.symbol : ''
                  )}
                </Typography>
              </div>
            </>
          ) : null}

          {!isPreOrder &&
            order.invoice &&
            order.invoice.detail.discount &&
            order.invoice.detail.discount.total_discount && (
              <Button
                className={`${classes.totalsRow} ${classes.discountButton}`}
                onClick={toggleDiscountDetail}
              >
                <Typography className={classes.discountText}>
                  <span className={classes.discountIcon}>
                    {discountDetailOpen ? <ArrowDropDown /> : <ArrowRight />}
                  </span>
                  <span>Dcto. total</span>
                </Typography>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <Typography className={classes.discountText}>
                    {`-${commaFormat(
                      Number(
                        order.invoice.detail.discount.total_discount
                          ? order.invoice.detail.discount.total_discount
                          : 0
                      ) * -1,
                      currency?.symbol
                    )}`}
                  </Typography>
                </div>
              </Button>
            )}
          <Collapse in={discountDetailOpen}>
            <>
              {!isPreOrder &&
                order.invoice &&
                order.invoice.detail.discount &&
                order.invoice.detail.discount.coupon_discount && (
                  <div className={classes.totalsRow}>
                    <Typography className={classes.labelDscto}>
                      Cupon (
                      {order.invoice.detail.discount.coupon_discount.code})
                    </Typography>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <Typography className={classes.labelDscto}>
                        {`-${commaFormat(
                          Number(
                            order.invoice.detail.discount.coupon_discount
                              .discount
                              ? order.invoice.detail.discount.coupon_discount
                                  .discount
                              : 0
                          ) * -1,
                          currency?.symbol
                        )}`}
                      </Typography>
                    </div>
                  </div>
                )}

              {!isPreOrder &&
                order.invoice &&
                order.invoice.detail.discount &&
                order.invoice.detail.discount.promotion_discount &&
                order.invoice.detail.discount.promotion_discount.map(promo => (
                  <div className={classes.totalsRow} key={promo.name}>
                    <Typography className={classes.labelDscto}>
                      {promo.name}
                    </Typography>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <Typography className={classes.labelDscto}>
                        {`-${commaFormat(
                          Number(promo.discount || 0) * -1,
                          currency?.symbol
                        )}`}
                      </Typography>
                    </div>
                  </div>
                ))}

              {!isPreOrder &&
                order.invoice &&
                order.invoice.detail.discount &&
                order.invoice.detail.discount.global_discount && (
                  <div className={classes.totalsRow}>
                    <Typography className={classes.labelDscto}>
                      dcto. Manual
                    </Typography>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                      <Typography className={classes.labelDscto}>
                        {`-${commaFormat(
                          Number(
                            order.invoice.detail.discount.global_discount
                              ? order.invoice.detail.discount.global_discount
                                  .discount
                              : 0
                          ) * -1,
                          currency?.symbol
                        )}`}
                      </Typography>
                    </div>
                  </div>
                )}
            </>
          </Collapse>

          {!isPreOrder && (
            <div className={classes.totalsRow}>
              <Typography className={classes.innerContentText}>
                {order.deliveryMethod === 'SHIPPING'
                  ? 'Envío'
                  : 'Recojo en tienda'}
              </Typography>
              <Typography className={classes.innerContentText}>
                {`${
                  orderShippingAmount >= 0
                    ? commaFormat(orderShippingAmount, currency?.symbol)
                    : '--'
                }`}
              </Typography>
            </div>
          )}

          {!isPreOrder &&
            order?.invoice?.detail?.discount?.shipping_promotion_discount && (
              <div className={classes.totalsRow}>
                <Typography className={classes.labelDscto}>
                  Promoción de envío
                </Typography>
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <Typography className={classes.labelDscto}>
                    {`-${commaFormat(
                      Number(
                        order?.invoice?.detail?.discount
                          ?.shipping_promotion_discount ?? 0
                      ) * -1,
                      currency?.symbol
                    )}`}
                  </Typography>
                </div>
              </div>
            )}

          {/*           {!isPreOrder &&
            order.invoice &&
            order.invoice.detail &&
            order.invoice.detail.taxes &&
            order.invoice.taxes &&
            order.invoice.taxes?.length &&
            null} */}

          {manualModification && !isPreOrder && (
            // (order.orderState === 'PAID' ||
            //   order.orderState === 'DELIVERED') &&
            <div className={classes.totalsRow}>
              <Typography className={classes.innerContentText}>
                Modificación manual
              </Typography>
              <Typography className={classes.innerContentText}>
                {manualModification}
              </Typography>
            </div>
          )}
          {order.invoice && (
            <>
              <Divider className={classes.divider} />
              <div className={classes.totalsRow}>
                <Typography
                  className={`${classes.innerContentText} ${classes.bold}`}
                >
                  Total
                </Typography>
                <Typography
                  className={`${classes.innerContentText} ${classes.bold}`}
                >
                  {`${
                    order.invoice.detail.total
                      ? commaFormat(
                          order.invoice.detail.total,
                          currency?.symbol
                        )
                      : 'Por confirmar'
                  }`}
                </Typography>
              </div>
            </>
          )}
        </div>
      </Grid>
      <Grid item xs={12} lg={6}>
        {!isPreOrder ? (
          <>
            {order.customer && (
              <div className={classes.innerOrderDetailContent}>
                <Typography className={classes.innerContentTitle}>
                  Información del Cliente{' '}
                  {canEditOrder && hasOrderEditCustomerDataFeature && (
                    <CustomButton
                      className={classes.editButton}
                      startIcon={<Edit />}
                      onClick={() => setEditCustomerInfoModal(true)}
                    >
                      Modificar
                    </CustomButton>
                  )}
                </Typography>
                <Divider className={classes.divider} />
                <div style={{display: 'flex'}}>
                  <Typography className={classes.innerContentText}>
                    {`${order.customer.firstName} ${order.customer.lastName}`}
                  </Typography>
                  {/* {true && (
                    <div className={classes.clientCategoryPill}>Super Vip</div>
                  )} */}
                </div>
                <Typography className={classes.innerContentText}>
                  {`${
                    order.customer.identificationNumber
                      ? `${order.customer.identificationNumber}`
                      : ''
                  }`}
                </Typography>
                <Typography className={classes.innerContentText}>
                  {order.customer.email}
                </Typography>
                <Typography className={classes.innerContentText}>
                  {parsePhoneNumber(`+${order.customer.phone}`)
                    ? parsePhoneNumber(
                        `+${order.customer.phone}`
                      ).formatInternational()
                    : `+${order.customer.phone}`}
                </Typography>
                <Divider className={classes.divider} />

                <CustomButton
                  href={generateWhatsappLink(order.customer.phone, '')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.whatsappButton}
                  startIcon={<img src={whatsAppIcon} alt="Whatsapp" />}
                >
                  Contactar
                </CustomButton>
              </div>
            )}

            <div className={classes.innerOrderDetailContent}>
              <Typography className={classes.innerContentTitle}>
                Método de entrega{' '}
                {canEditOrder &&
                  hasOrderEditDeliveryDataFeature &&
                  order.deliveryMethod !== 'NONE' && (
                    <CustomButton
                      className={classes.editButton}
                      startIcon={<Edit />}
                      onClick={() => setEditDeliveryInfoModal(true)}
                    >
                      Modificar
                    </CustomButton>
                  )}
              </Typography>
              <Divider className={classes.divider} />
              <Typography
                className={`${classes.innerContentText} ${classes.bold}`}
              >
                {order.deliveryMethod === 'SHIPPING'
                  ? 'Delivery'
                  : order.deliveryMethod === 'PICK_UP'
                  ? 'Recojo en tienda'
                  : ''}
              </Typography>
              {order.deliveryMethod === 'SHIPPING' && (
                <>
                  <div className={classes.innerContentText}>
                    {shippingAddress} - {order.shippingAddress?.country?.name}
                  </div>
                  {order.shippingDetail &&
                    order.shippingDetail.shippingAddressType === 'GEO' && (
                      <CustomButton onClick={handleOpenMapModal}>
                        Ver mapa
                      </CustomButton>
                    )}
                </>
              )}
              {order.shippingDetail && order.invoice && (
                <div>
                  <div className={classes.totalsRow}>
                    <Typography
                      className={`${classes.innerContentText} ${classes.bold}`}
                    >
                      Tarifa
                    </Typography>
                    <Typography
                      className={classes.flatRateAmount}
                      style={{fontWeight: 500}}
                    >
                      {commaFormat(
                        order.invoice.detail.last_mile_shipping,
                        currency?.symbol
                      )}
                    </Typography>
                  </div>
                  <Typography className={classes.innerContentText}>
                    {order.shippingDetail && order.shippingDetail.flatRateLabel}
                  </Typography>
                </div>
              )}

              {order.deliveryMethod === 'NONE' && (
                <div className={classes.innerContentText}>
                  Esta orden no tiene método de entrega
                </div>
              )}
            </div>

            {order.additionalInformation &&
              order.additionalInformation.length > 0 && (
                <div className={classes.innerOrderDetailContent}>
                  <Typography className={classes.innerContentTitle}>
                    Información Adicional{' '}
                    {canEditOrder && hasOrderEditAdditionalInformationFeature && (
                      <CustomButton
                        className={classes.editButton}
                        startIcon={<Edit />}
                        onClick={() => setEditAdditionalInfoModal(true)}
                      >
                        Modificar
                      </CustomButton>
                    )}
                  </Typography>
                  <Divider className={classes.divider} />
                  <div className={classes.additionalInformationContent}>
                    {sortedAdditionalInformation?.map((oa, index: number) => {
                      const additionalInformationValue = buildAdditionalInformationInfo(
                        oa
                      );
                      return (
                        <div
                          key={`oa-${index}`}
                          className={classes.additionalInformationItem}
                        >
                          <div className={classes.additionalInformationTitle}>
                            <Typography
                              className={`${classes.innerContentText} ${classes.bold}`}
                            >
                              {oa.storeOrderAdditionalInformation.name}:
                            </Typography>
                            <TiendadaTooltip
                              placement="top"
                              title={`Copiar ${oa.storeOrderAdditionalInformation.name}`}
                            >
                              <IconButton
                                size="medium"
                                onClick={() => {
                                  setCopyClipboardSuccess(false);
                                  copyTextToClipboard(
                                    additionalInformationValue
                                  )
                                    .then(() => {
                                      setCopyClipboardSuccess(true);
                                    })
                                    .catch(() => {
                                      setCopyClipboardSuccess(false);
                                    });
                                }}
                                className={
                                  classes.additionalInformationCopyButton
                                }
                              >
                                <CopyIcon />
                              </IconButton>
                            </TiendadaTooltip>
                          </div>
                          <Typography
                            className={classes.additionalInformationItemValue}
                          >
                            {additionalInformationValue}
                          </Typography>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
          </>
        ) : (
          <div style={{padding: '16px'}}>
            <Typography className={classes.innerContentTitle}>
              ¿Cómo funciona un pre pedido?
            </Typography>
            <img
              src={preOrderSteps}
              alt=""
              style={{marginTop: '16px', alignSelf: 'start'}}
            />
          </div>
        )}
      </Grid>
    </Grid>
  );

  const hasTaxes = order?.paymentInvoice?.taxes?.length > 0;
  let paymentMethodName = paymentMethod?.displayName || '';

  if (
    paymentMethod &&
    (paymentMethod.paymentMethod === PAY_ON_DELIVERY ||
      paymentMethod.paymentMethod === CONTINUE_TO_CHAT)
  ) {
    paymentMethodName = MAP_PAYMENT_METHOD[paymentMethod.paymentMethod];
  }

  return (
    <div className={classes.root}>
      <SnackBar mode="error" message={updateOrderStateErrorMessage} />
      <SnackBar mode="error" message={messageError} />
      <SnackBar mode="error" message={editOrderError} />
      <SnackBar
        mode="success"
        message={saveOrderCommentSuccess ? 'Comentario guardado' : ''}
      />
      <SnackBar
        mode="success"
        message={deleteOrderCommentSuccess ? 'Comentario eliminado' : ''}
      />
      <SnackBar
        mode="success"
        message={updateOrderCommentSuccess ? 'Comentario actualizado' : ''}
      />
      <SnackBar
        mode="success"
        message={copyClipboardSuccess ? 'Copiado al portapapeles' : ''}
      />
      {/* <SnackBar mode="error" message={saveOrderCommentError} />
      <SnackBar mode="error" message={deleteOrderCommentError} /> */}
      {sendEmailSuccess && (
        <SnackBar mode="success" message={'Se envió correctamente el correo'} />
      )}
      <CustomAlert
        open={!!alertOpen}
        className={classes.alert}
        severity="success"
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={() => {
              setAlertOpen('');
            }}
          >
            <Close fontSize="inherit" />
          </IconButton>
        }
      >
        {alertOpen}
      </CustomAlert>
      <CustomAlert
        open={!!alertPayment}
        className={classes.alert}
        severity="error"
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={() => {
              setAlertPayment('');
            }}
          >
            <Close fontSize="inherit" />
          </IconButton>
        }
      >
        {alertPayment}
      </CustomAlert>
      <Snackbar
        open={stateChangeAlertOpen}
        anchorOrigin={
          !tablet
            ? {
                vertical: 'bottom',
                horizontal: 'left',
              }
            : {vertical: 'top', horizontal: 'center'}
        }
        autoHideDuration={6000}
        onClose={() => setStateChangeAlertOpen(false)}
      >
        <div className={classes.stateChangeAlert}>
          <InfoOutlined />
          <Typography className={classes.stateChangeAlertTitle}>
            Cambio de estado exitoso
          </Typography>
          <div className={classes.alertStatePill}>{stateString}</div>
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            style={{position: 'absolute', top: 8, right: 8}}
            onClick={() => {
              setStateChangeAlertOpen(false);
            }}
          >
            <Close fontSize="inherit" />
          </IconButton>
        </div>
      </Snackbar>
      <AlertDueDateTier />
      <Switch>
        <Route exact path="/orders/:orderId">
          <div className={classes.headerWrapper}>
            <Typography className={classes.title}>
              <IconButton
                className={classes.backButton}
                onClick={() => history.goBack()}
              >
                <ArrowBack />
              </IconButton>
              Detalle de Pedido
            </Typography>
            {!storeHasOrderStatusChangeFeature && (
              <span className={classes.chipPRO}>PRO</span>
            )}
          </div>

          {order && !isFetching && (
            <main className={classes.content}>
              <div className={classes.orderLeftContent}>
                {orderStatePill}
                <Typography className={classes.orderNumber}>
                  {`Nº Orden ${order.id}`}
                </Typography>
                <Typography
                  className={classes.date}
                >{`Fecha: ${dateString}`}</Typography>

                {hasTaxes && <OrderTaxInvoiceSection order={order} />}

                <Hidden smUp>
                  {orderStateIndex >= getOrderStateIndex('PAID') && (
                    <Grid container justify="space-around" space={1}>
                      <Grid item xs={12} sm={12} style={{textAlign: 'center'}}>
                        {order.voucher && (
                          <CustomButton
                            className={classes.buttonComprobante}
                            style={{width: '90%'}}
                            variant="outlined"
                            to={`/orders/${order.id}/emit-receipt`}
                            startIcon={<img src={receiptIcon} alt="Whatsapp" />}
                          >
                            <span>Emitir Comprobante</span>
                          </CustomButton>
                        )}
                      </Grid>
                      {order.shippingDetail &&
                        order.customer &&
                        urlLogo &&
                        store && (
                          <Grid item xs={12} sm={12}>
                            <ShippingLabel
                              storeLogo={urlLogo}
                              storeName={store.name}
                              customer={order.customer}
                              order={order}
                            />
                          </Grid>
                        )}
                    </Grid>
                  )}
                </Hidden>
                {!!paymentMethod && order.invoice && (
                  <>
                    {orderStateIndex > getOrderStateIndex('PAID') && (
                      <>
                        <br />
                        <Grid container className={classes.cardContent}>
                          <Typography className={classes.innerContentTitle}>
                            Monto recibido
                          </Typography>
                          <Divider className={classes.divider} />
                          <Grid item>
                            <Typography className={classes.innerContentText}>
                              {commaFormat(
                                order.invoice.detail.total,
                                currency?.symbol
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      </>
                    )}
                    <br />
                    <Grid container className={classes.cardContent}>
                      <Typography className={classes.innerContentTitle}>
                        Información de pago
                        {canEditOrder && hasOrderEditPaymentDataFeature && (
                          <CustomButton
                            className={classes.editButton}
                            startIcon={<Edit />}
                            onClick={() => openConfirmModal()}
                          >
                            Modificar
                          </CustomButton>
                        )}
                      </Typography>
                      <Divider className={classes.divider} />
                      <Grid item>
                        <Typography className={classes.innerContentText}>
                          {`Método de pago:  ${paymentMethodName}`}
                        </Typography>
                      </Grid>
                    </Grid>
                    <br />
                    <Grid
                      container
                      className={classes.cardContent}
                      style={{flexDirection: 'column', alignItems: 'stretch'}}
                    >
                      <Typography className={classes.innerContentTitle}>
                        Detalle de canal de ventas
                      </Typography>
                      <Divider className={classes.divider} />
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography className={classes.innerContentText}>
                          Canal de ventas
                        </Typography>
                        <div className={classes.manualOrderBadge}>
                          {{
                            DEFAULT: 'WEB',
                            MANUAL: 'MANUAL',
                            PRE_ORDER: 'MANUAL',
                          }[order.orderCreationType] || ''}
                        </div>
                      </div>
                      {/* <div
                        style={{
                          marginTop: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography className={classes.innerContentText}>
                          Vendedor
                        </Typography>
                        <Typography
                          className={classes.innerContentText}
                          style={{fontWeight: 500}}
                        >
                          Joaquin Gutierrez
                        </Typography>
                      </div> */}
                      {userHasOrderEditCollaboratorFeature ? (
                        <CustomSelect
                          className={classes.saleChannelDropdown}
                          label="Colaborador"
                          value={order.collaboratorId || ''}
                          onChange={e =>
                            handleChangeOrderCollaborator(
                              order.id,
                              e.target.value
                            )
                          }
                        >
                          {collaborators &&
                            collaborators.map(collaborator => (
                              <MenuItem
                                value={collaborator.id}
                                key={collaborator.id}
                              >
                                {collaborator.firstName +
                                  ' ' +
                                  collaborator.lastName}
                              </MenuItem>
                            ))}
                        </CustomSelect>
                      ) : (
                        order.collaboratorId &&
                        collaborator &&
                        order.collaboratorId === collaborator.id && (
                          <div
                            style={{
                              marginTop: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography className={classes.innerContentText}>
                              Colaborador
                            </Typography>
                            <Typography
                              className={classes.innerContentText}
                              style={{
                                marginLeft: '16px',
                                fontWeight: 500,
                                textAlign: 'right',
                              }}
                            >
                              {`${collaborator.firstName} ${collaborator.lastName}`}
                            </Typography>
                          </div>
                        )
                      )}
                      {orderComments && !!orderComments.length && (
                        <>
                          <Divider
                            className={classes.divider}
                            style={{marginBottom: 0}}
                          />
                          {orderComments.map(
                            (comment, i) =>
                              i < 2 && (
                                <React.Fragment key={`comment-${i}`}>
                                  <div
                                    style={{
                                      width: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <Typography
                                      className={classes.commentLogDate}
                                    >
                                      {new Intl.DateTimeFormat('es-PE', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: '2-digit',
                                      }).format(comment.createdAt)}{' '}
                                      - {comment.userName}
                                    </Typography>
                                    {user && comment.userId === user.id && (
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <IconButton
                                          style={{
                                            border:
                                              editingCommentIndex === i
                                                ? `1px solid ${colors.purple3}`
                                                : undefined,
                                          }}
                                          className={classes.orderCommentButton}
                                          onClick={() =>
                                            handleClickEditOrderComment(i)
                                          }
                                        >
                                          <Edit
                                            className={
                                              classes.orderCommentButtonIcon
                                            }
                                          />
                                        </IconButton>
                                        <IconButton
                                          className={classes.orderCommentButton}
                                          color="default"
                                          onClick={() =>
                                            handleDeleteOrderComment(comment)
                                          }
                                        >
                                          <DeleteOutline
                                            color="default"
                                            className={
                                              classes.orderCommentButtonIcon
                                            }
                                          />
                                        </IconButton>
                                      </div>
                                    )}
                                  </div>
                                  {editingCommentIndex !== i ? (
                                    <Typography
                                      className={classes.innerContentText}
                                    >
                                      {comment.comment}
                                    </Typography>
                                  ) : (
                                    <>
                                      <CustomTextField
                                        className={classes.saleChannelTextfield}
                                        multiline
                                        minRows={3}
                                        name="editComment"
                                        value={editingCommentValue}
                                        onChange={e =>
                                          setEditingCommentValue(e.target.value)
                                        }
                                      />
                                      <CustomButton
                                        className={classes.saleChannelButton}
                                        variant="outlined"
                                        onClick={handleSaveOrderEditingComment}
                                      >
                                        Guardar
                                      </CustomButton>
                                    </>
                                  )}
                                </React.Fragment>
                              )
                          )}
                          <Divider className={classes.divider} />
                          {orderComments && orderComments.length > 2 && (
                            <>
                              <CustomButton
                                color="default"
                                className={classes.commentsSeeMoreButton}
                                onClick={() =>
                                  setSeeMoreCommentsModalOpen(true)
                                }
                              >
                                Ver más
                              </CustomButton>
                              <Divider className={classes.divider} />
                              <CustomModal
                                open={seeMoreCommentsModalOpen}
                                handleCloseModal={() =>
                                  setSeeMoreCommentsModalOpen(false)
                                }
                              >
                                <div className={classes.seeMoreCommentsModal}>
                                  <Typography
                                    className={
                                      classes.seeMoreCommentsModalTitle
                                    }
                                  >
                                    Comentarios
                                  </Typography>
                                  {orderComments.map((comment, i) => (
                                    <React.Fragment key={`comment-${i}`}>
                                      <div
                                        style={{
                                          width: '100%',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                        }}
                                      >
                                        <Typography
                                          className={classes.commentLogDate}
                                        >
                                          {new Intl.DateTimeFormat('es-PE', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: '2-digit',
                                          }).format(comment.createdAt)}{' '}
                                          - {comment.userName}
                                        </Typography>
                                        {user && comment.userId === user.id && (
                                          <div
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                            }}
                                          >
                                            <IconButton
                                              style={{
                                                border:
                                                  editingCommentIndex === i
                                                    ? `1px solid ${colors.purple3}`
                                                    : undefined,
                                              }}
                                              className={
                                                classes.orderCommentButton
                                              }
                                              onClick={() =>
                                                handleClickEditOrderComment(i)
                                              }
                                            >
                                              <Edit
                                                className={
                                                  classes.orderCommentButtonIcon
                                                }
                                              />
                                            </IconButton>
                                            <IconButton
                                              className={
                                                classes.orderCommentButton
                                              }
                                              color="default"
                                              onClick={() =>
                                                handleDeleteOrderComment(
                                                  comment
                                                )
                                              }
                                            >
                                              <DeleteOutline
                                                color="default"
                                                className={
                                                  classes.orderCommentButtonIcon
                                                }
                                              />
                                            </IconButton>
                                          </div>
                                        )}
                                      </div>
                                      {editingCommentIndex !== i ? (
                                        <Typography
                                          className={classes.innerContentText}
                                        >
                                          {comment.comment}
                                        </Typography>
                                      ) : (
                                        <>
                                          <CustomTextField
                                            className={
                                              classes.saleChannelTextfield
                                            }
                                            multiline
                                            minRows={3}
                                            name="editComment"
                                            value={editingCommentValue}
                                            onChange={e =>
                                              setEditingCommentValue(
                                                e.target.value
                                              )
                                            }
                                          />
                                          <CustomButton
                                            className={
                                              classes.saleChannelButton
                                            }
                                            variant="outlined"
                                            onClick={
                                              handleSaveOrderEditingComment
                                            }
                                          >
                                            Guardar
                                          </CustomButton>
                                        </>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </div>
                              </CustomModal>
                            </>
                          )}
                        </>
                      )}
                      {userHasOrderCommentsFeature && (
                        <>
                          <CustomTextField
                            className={classes.saleChannelTextfield}
                            label="Agregar comentario"
                            multiline
                            minRows={3}
                            name="newComment"
                            value={commentValue}
                            onChange={e => setCommentValue(e.target.value)}
                          />
                          <CustomButton
                            className={classes.saleChannelButton}
                            variant="outlined"
                            onClick={handleSaveOrderComment}
                          >
                            Enviar
                          </CustomButton>
                        </>
                      )}
                    </Grid>
                    <br />
                  </>
                )}
                {order.invoice &&
                  paymentMethod &&
                  (paymentMethod.paymentMethod === 'CARD' ||
                    paymentMethod.paymentMethod === 'OTHERS') &&
                  order.invoice.invoiceState === 'PENDING' &&
                  order.token && (
                    <div className={classes.copyLinkArea}>
                      <ClickAwayListener
                        onClickAway={handlePaymentTooltipClose}
                      >
                        <Tooltip
                          PopperProps={{
                            disablePortal: true,
                          }}
                          onClose={handlePaymentTooltipClose}
                          open={openPaymentTooltip}
                          disableFocusListener
                          disableHoverListener
                          disableTouchListener
                          leaveDelay={300}
                          title="Enlace copiado"
                        >
                          <div style={{marginTop: 8}}>
                            <Button
                              className={classes.copyLink}
                              onClick={() => {
                                const textCopied = store
                                  ? `https://${getCustomDomain(store)}/order/${
                                      order.id
                                    }?token=${order.token}`
                                  : '';
                                store &&
                                  navigator &&
                                  navigator.clipboard &&
                                  navigator.clipboard
                                    .writeText(textCopied)
                                    .then(() => {
                                      setOpenPaymentTooltip(true);
                                    })
                                    .catch(e => console.error(e));
                              }}
                            >
                              Copiar link de pago &nbsp;
                              <img
                                src={copyIcon}
                                alt=""
                                width={18}
                                height={18}
                              />
                            </Button>
                          </div>
                        </Tooltip>
                      </ClickAwayListener>

                      <CustomButton
                        className={classes.copyLinkButton}
                        variant="contained"
                        color="primary"
                        disableElevation
                        onClick={() =>
                          sendPaymentLink(
                            store
                              ? `https://${getCustomDomain(store)}/order/${
                                  order.id
                                }?token=${order.token}`
                              : ''
                          )
                        }
                        startIcon={<Send />}
                      >
                        Enviar enlace de pago
                      </CustomButton>
                      {/* </Tooltip>
                      </ClickAwayListener> */}
                      <Typography className={classes.copyLinkText}>
                        *Envía este link a tu cliente para que pueda concretar
                        el pago.
                      </Typography>
                    </div>
                  )}
                {order.invoice &&
                  paymentMethod &&
                  paymentMethod.paymentMethod === 'CONTINUE_TO_CHAT' &&
                  order.invoice.invoiceState === 'PENDING' &&
                  order.token && (
                    <div className={classes.copyLinkArea}>
                      <CustomButton
                        className={classes.copyLinkButton}
                        variant="contained"
                        color="primary"
                        disableElevation
                        onClick={() =>
                          sendPaymentLink(
                            store
                              ? `https://${getCustomDomain(store)}/order/${
                                  order.id
                                }/payment-method?token=${order.token}`
                              : ''
                          )
                        }
                        startIcon={<Send />}
                      >
                        Enviar enlace de pago
                      </CustomButton>
                      <Typography className={classes.copyLinkText}>
                        *Envía este link a tu cliente para que pueda concretar
                        el pago.
                      </Typography>
                    </div>
                  )}
                {isPreOrder &&
                  order.preOrderState &&
                  order.preOrderState !== 'CANCELLED' &&
                  order.token && (
                    <div className={classes.copyLinkArea}>
                      <ClickAwayListener onClickAway={handleTooltipClose}>
                        <Tooltip
                          PopperProps={{
                            disablePortal: true,
                          }}
                          onClose={handleTooltipClose}
                          open={openToolTip}
                          disableFocusListener
                          disableHoverListener
                          disableTouchListener
                          leaveDelay={300}
                          title="Enlace copiado"
                        >
                          <div>
                            <CustomButton
                              className={classes.copyLinkButton}
                              variant="contained"
                              color="primary"
                              startIcon={<Send />}
                              onClick={() => {
                                const textCopied = store
                                  ? `https://${getCustomDomain(
                                      store
                                    )}/checkout/information?po_id=${
                                      order.id
                                    }&po_token=${order.token}`
                                  : '';

                                console.log(textCopied);

                                navigator &&
                                  navigator.clipboard &&
                                  navigator.clipboard
                                    .writeText(textCopied)
                                    .then(() => {
                                      setOpenToolTip(true);
                                    })
                                    .catch(e => console.error(e));
                              }}
                            >
                              Copiar link de pre pedido
                            </CustomButton>
                          </div>
                        </Tooltip>
                      </ClickAwayListener>
                      <Typography className={classes.copyLinkText}>
                        *Envía este link a tu cliente para que llene sus datos y
                        proceda a pagar.
                      </Typography>
                    </div>
                  )}
                <Hidden xsDown>
                  {invoiceReceiptImage && (
                    <div style={{marginTop: 8}}>
                      <CustomButton
                        className={classes.receipt}
                        startIcon={<AttachFile />}
                        href={invoiceReceiptImage}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver Comprobante
                      </CustomButton>
                      <br />
                    </div>
                  )}
                  {order.orderState &&
                    order.orderState === 'DELIVERED' &&
                    !order.orderReview && (
                      <Grid
                        container
                        direction="column"
                        alignItems="center"
                        className={classes.reviewsWrap}
                      >
                        <Grid item>
                          <CustomButton
                            startIcon={<SendIcon />}
                            className={classes.reviewButton}
                            variant="contained"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={generateWhatsappLink(
                              order.customer.phone,
                              reviewMessage
                            )}
                          >
                            Enviar Reseña por Whatsapp
                          </CustomButton>
                        </Grid>

                        <Grid
                          item
                          container
                          alignItems="center"
                          className={classes.copyLinkWrap}
                        >
                          {store && (
                            <ClickAwayListener onClickAway={handleTooltipClose}>
                              <div>
                                <Tooltip
                                  PopperProps={{
                                    disablePortal: true,
                                  }}
                                  onClose={handleTooltipClose}
                                  open={openToolTip}
                                  disableFocusListener
                                  disableHoverListener
                                  disableTouchListener
                                  leaveDelay={300}
                                  title="Enlace copiado"
                                >
                                  <Button
                                    className={classes.copyLink}
                                    onClick={() => {
                                      const textCopied = store
                                        ? `https://${getCustomDomain(
                                            store
                                          )}/order/${order.id}/review?token=${
                                            order.token
                                          }`
                                        : '';
                                      store &&
                                        navigator &&
                                        navigator.clipboard &&
                                        navigator.clipboard
                                          .writeText(textCopied)
                                          .then(() => {
                                            setOpenToolTip(true);
                                          })
                                          .catch(e => console.error(e));
                                    }}
                                  >
                                    Copiar link de reseña &nbsp;
                                    <img
                                      src={copyIcon}
                                      alt=""
                                      width={18}
                                      height={18}
                                    />
                                  </Button>
                                </Tooltip>
                              </div>
                            </ClickAwayListener>
                          )}
                        </Grid>
                      </Grid>
                    )}
                </Hidden>
                <Hidden smUp>{orderDetailsContent}</Hidden>
                <Hidden xsDown>
                  {isPreOrder && <PreOrderAdvice />}

                  {stateChangeButton}
                  {/* {stateRevertButton} */}
                  {cancelButton}
                </Hidden>
              </div>
              <Hidden xsDown>
                <div className={classes.orderRightContent}>
                  {orderDetailsContent}
                </div>
              </Hidden>
              <Hidden smUp>
                <br />
                {invoiceReceiptImage && (
                  <>
                    <CustomButton
                      className={classes.receipt}
                      startIcon={<AttachFile />}
                      href={invoiceReceiptImage}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Ver Comprobante
                    </CustomButton>
                  </>
                )}

                {order.orderState === 'DELIVERED' && !order.orderReview && (
                  <Grid
                    container
                    direction="column"
                    alignItems="center"
                    className={classes.reviewsWrap}
                  >
                    <Grid item>
                      <CustomButton
                        startIcon={<SendIcon />}
                        className={classes.reviewButton}
                        variant="contained"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={generateWhatsappLink(
                          order.customer.phone,
                          reviewMessage
                        )}
                      >
                        Enviar Reseña por Whatsapp
                      </CustomButton>
                    </Grid>
                    <Grid
                      item
                      container
                      alignItems="center"
                      className={classes.copyLinkWrap}
                    >
                      {store && (
                        <ClickAwayListener onClickAway={handleTooltipClose}>
                          <div>
                            <Tooltip
                              PopperProps={{
                                disablePortal: true,
                              }}
                              onClose={handleTooltipClose}
                              open={openToolTip}
                              disableFocusListener
                              disableHoverListener
                              disableTouchListener
                              leaveDelay={300}
                              title="Enlace copiado"
                            >
                              <Button
                                className={classes.copyLink}
                                onClick={() => {
                                  const textCopied = store
                                    ? `https://${getCustomDomain(
                                        store
                                      )}/order/${order.id}/review?token=${
                                        order.token
                                      }`
                                    : '';
                                  store &&
                                    navigator &&
                                    navigator.clipboard &&
                                    navigator.clipboard
                                      .writeText(textCopied)
                                      .then(() => {
                                        setOpenToolTip(true);
                                      })
                                      .catch(e => console.error(e));
                                }}
                              >
                                Copiar link de reseña &nbsp;
                                <img
                                  src={copyIcon}
                                  alt=""
                                  width={18}
                                  height={18}
                                />
                              </Button>
                            </Tooltip>
                          </div>
                        </ClickAwayListener>
                      )}
                    </Grid>
                  </Grid>
                )}
                {isPreOrder && <PreOrderAdvice />}
                {stateChangeButton}
                {/* {stateRevertButton} */}
                {cancelButton}
              </Hidden>
            </main>
          )}
        </Route>
        <Route
          path="/orders/:orderId/edit"
          component={EditOrderItemsContainer}
        />
        <Route
          path="/orders/:orderId/emit-receipt"
          component={ReceiptContainer}
        />
      </Switch>
      <TierRestrictionModal
        open={tierRestrictionModal}
        handleClose={() => setTierRestrictionModal(false)}
      />
      {order && (
        <>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
          >
            {({TransitionProps, placement}) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === 'bottom' ? 'center top' : 'center bottom',
                }}
              >
                <Paper className={classes.statesMenuPaper}>
                  <ClickAwayListener onClickAway={handleCloseStatesMenu}>
                    <MenuList
                      autoFocusItem={open}
                      className={classes.statesMenu}
                      // onKeyDown={handleListKeyDown}
                    >
                      {availableOrderStates &&
                        canGoToPrevState &&
                        availableOrderStates.statesToRevert.map(state => (
                          <MenuItem
                            key={`${state}-menu-item`}
                            className={classes.statesMenuItem}
                            onClick={() => handleChangeState(state)}
                          >
                            <div
                              style={{
                                height: 14,
                                width: 14,
                                margin: '8px 10px 8px 0',
                                borderRadius: '50%',
                                backgroundColor: ORDER_STATE_COLORS[state].main,
                              }}
                            />
                            {ORDER_STATE_STRINGS[state]}
                          </MenuItem>
                        ))}
                      {availableOrderStates &&
                        availableOrderStates.statesToProceed.map(state => (
                          <MenuItem
                            key={`${state}-menu-item`}
                            className={classes.statesMenuItem}
                            onClick={() => handleChangeState(state)}
                          >
                            <div
                              style={{
                                height: 14,
                                width: 14,
                                margin: '8px 10px 8px 0',
                                borderRadius: '50%',
                                backgroundColor: ORDER_STATE_COLORS[state].main,
                              }}
                            />
                            {ORDER_STATE_STRINGS[state]}
                          </MenuItem>
                        ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
          {order.invoice && (
            <PendingModal
              open={pendingModalOpen}
              handleClose={handlePendingModalClose}
              order={order}
              updateOrderStateWithErrorCheck={updateOrderStateWithErrorCheck}
              isSaving={isSaving}
              isFetchingOrderStockErrors={isFetchingOrderStockErrors}
            />
          )}
          <StockErrorsModal
            open={openStockErrorsModal}
            order={order}
            updateOrderStateRequest={updateOrderStateRequest}
            orderStockErrors={orderStockErrors}
            handleConfirm={handleStockErrorsConfirm}
            handleClose={() => setOpenStockErrorsModal(false)}
          />

          {!!editStockErrors && editStockErrors.length > 0 && (
            <EditOrderProductsErrorsModal
              open
              order={order}
              orderStockErrors={editStockErrors}
              handleConfirm={handleConfirmGoToEdit}
              handleClose={() => {
                setEditStockErrors(null);
              }}
            />
          )}

          <EmailModal
            open={emailModalOpen}
            handleClose={handleEmailModalClose}
          />
          <CancelModal
            open={cancelModalOpen}
            handleClose={handleCancelModalClose}
            order={order}
            handleUpdateOrderState={handleUpdateOrderState}
          />
          {order.shippingDetail &&
            order.shippingDetail.shippingAddressType === 'GEO' &&
            order.shippingDetail.shippingAddressGeo && (
              <MapModal
                open={openMapModal}
                shippingAddressGeo={order.shippingDetail.shippingAddressGeo}
                handleCloseModal={() => setOpenMapModal(false)}
              />
            )}
          {!isPreOrder && (
            <>
              {order.customer && (
                <EditCustomerInfoModal
                  open={editCustomerInfoModal}
                  handleClose={() => setEditCustomerInfoModal(false)}
                  order={order}
                  isSaving={isSaving}
                />
              )}
              <EditDeliveryInfoModal
                open={editDeliveryInfoModal}
                handleClose={() => setEditDeliveryInfoModal(false)}
                order={order}
                isSaving={isSaving}
              />
              <EditBillingInfoModal
                open={editBillingInfoModal}
                handleClose={() => setEditBillingInfoModal(false)}
                order={order}
                isSaving={isSaving}
              />
              <ConfirmModal
                open={confirmModalOpen}
                onClose={closeConfirmModal}
                onCancel={closeConfirmModal}
                onConfirm={() => {
                  closeConfirmModal();
                  setEditPaymentInfoModal(true);
                }}
                message="El comprobante adjunto se borrará."
              />
              <EditPaymentInfoModal
                open={editPaymentInfoModal}
                handleClose={() => setEditPaymentInfoModal(false)}
                order={order}
                isSaving={isSaving}
              />
              {editAdditionalInfoModal && (
                <AdditionalInformationProvider>
                  <EditAdditionalInfoModal
                    open={editAdditionalInfoModal}
                    handleClose={() => setEditAdditionalInfoModal(false)}
                    order={order}
                    isSaving={isSaving}
                  />
                </AdditionalInformationProvider>
              )}
            </>
          )}
          {!!updateOrderStateSuccess &&
            hasOrderStateChangeMessageFeature &&
            updateOrderStateSuccess.type === 'order' && (
              <StateChangeMessageModal
                open={stateChangeMessageModalOpen}
                order={updateOrderStateSuccess}
                onClose={handleChangeMessageModalClose}
              />
            )}
        </>
      )}
    </div>
  );
}
